import {useEffect, useRef} from 'react'

// PWA OOTB Hooks
import {
    useShopperContext,
    useShopperContextsMutation,
    useUsid,
    useAccessToken,
    useCustomerType
} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

// default hooks
import useMultiSite from './use-multi-site'

// custom hooks
import useShipToCountry from '../hooks/use-ship-to-country'
import useSiteType from '../hooks/use-site-type'

// utils
import {getShipToCountry} from '../utils/ship-to-country-utils'
import {clearLocalStorageJSONItem, getLocalStorageJSONItem} from '@app/utils/utils'

/**
 * Updates the shopper context after a user logs out, ensuring the context reflects the current shipping country, locale, and site.
 * Depending on the presence of custom qualifiers in the response, it either updates or creates a new shopper context.
 *
 * @async
 * @param {Object} params - Parameters for updating the shopper context.
 * @param {Object} params.currentShipToCountry - The country object to which items will be shipped, containing at least `id` and `currency`.
 * @param {Object} params.locale - The locale object, typically containing an `id` property.
 * @param {Object} params.updateShopperContext - Object with a `mutateAsync` method to update the shopper context.
 * @param {Object} params.createShopperContext - Object with a `mutateAsync` method to create a new shopper context.
 * @param {Function} params.getUsidWhenReady - Async function that resolves to the current USID (user session ID).
 * @param {Object} params.site - The current site object, containing at least an `id` property.
 * @param {Object} params.shopperContext - Current shopper context returned by SCAPI, when available.
 * @returns {Promise<void>} Resolves when the shopper context has been updated or created.
 */
async function updateShopperContextAfterLogout({
    currentShipToCountry,
    locale,
    updateShopperContext,
    createShopperContext,
    getUsidWhenReady,
    site,
    shopperContext
}) {
    if (!currentShipToCountry) return
    const usid = await getUsidWhenReady()

    const options = {
        parameters: {
            usid: usid,
            siteId: site.id
        },
        body: {
            geoLocation: {
                countryCode: currentShipToCountry?.id
            },
            customQualifiers: {
                currency: currentShipToCountry?.currency,
                locale: locale?.id
            }
        }
    }
    const hasExistingShopperContext = Boolean(
        shopperContext?.geoLocation || shopperContext?.customQualifiers
    )

    if (hasExistingShopperContext) {
        await updateShopperContext.mutateAsync(options)
    } else {
        await createShopperContext.mutateAsync(options)
    }
}

/**
 * Custom hook to manage and update the shopper context based on customer type and shipping country.
 * This hook tracks changes in customer type and shipping country, and updates the shopper context
 * accordingly. It provides a method to update the shopper context after a user logs out.
 *
 * @param {Object} [options] - Hook behavior options.
 * @param {boolean} [options.persistOnShipToCountryChange=false] - Whether this hook instance should automatically persist the active ship-to country.
 * @returns {Object} An object containing:
 * - {Function} updateShopperContextAfterLogout: A function to update the shopper context after logout,
 *   which includes parameters such as user session ID, customer ID, current shipping country, locale,
 *   and other necessary API functions.
 *
 * @example
 * const { updateShopperContextAfterLogout } = useUpdateShopperContext();
 * updateShopperContextAfterLogout();
 */
const useUpdateShopperContext = ({persistOnShipToCountryChange = false} = {}) => {
    const {shipToCountry} = useShipToCountry()
    const updateShopperContext = useShopperContextsMutation('updateShopperContext')
    const createShopperContext = useShopperContextsMutation('createShopperContext')
    const {site, locale} = useMultiSite()
    const {isUS} = useSiteType()
    const {customerType} = useCustomerType()
    const {token} = useAccessToken()
    const {usid, getUsidWhenReady} = useUsid()
    const {data: shopperContext, isLoading: isShopperContextLoading} = useShopperContext(
        {
            parameters: {usid}
        },
        {enabled: Boolean(!isServer && usid)}
    )

    // Ref to store the previous shipToCountry when the user is logged in
    const previousShipToCountryRef = useRef(null)

    // Ref to track if the initial load is complete
    const initialLoadCompleteRef = useRef(false)
    const lastPersistedContextRef = useRef(null)

    /**
     * Persists the active ship-to country into shopper context after SSR hydration.
     *
     * @param {Object} currentShipToCountry - Ship-to country object with id and currency.
     * @returns {Promise<void>} Resolves when shopper context is created or updated.
     */
    const persistShopperContextCountry = async (currentShipToCountry) =>
        updateShopperContextAfterLogout({
            currentShipToCountry,
            locale,
            updateShopperContext,
            createShopperContext,
            getUsidWhenReady,
            site,
            shopperContext
        })

    useEffect(() => {
        if (!initialLoadCompleteRef.current) {
            // Set the initial load complete after the first render
            initialLoadCompleteRef.current = true
            return
        }
        // Only update if the token or customerType has changed, or if the shipToCountry has changed
        if (shipToCountry?.id && previousShipToCountryRef?.current?.id === shipToCountry?.id) return
        previousShipToCountryRef.current = shipToCountry
    }, [token, customerType, shipToCountry])

    useEffect(() => {
        if (
            !persistOnShipToCountryChange ||
            isServer ||
            isShopperContextLoading ||
            !usid ||
            !site?.id ||
            !locale?.id ||
            !shipToCountry?.id
        ) {
            return
        }

        const shopperContextCountry =
            shopperContext?.geoLocation?.countryCode || shopperContext?.geoLocation?.country
        const shopperContextCurrency = shopperContext?.customQualifiers?.currency
        const shopperContextLocale = shopperContext?.customQualifiers?.locale
        const countryCodeStillSetting = getLocalStorageJSONItem('countryCode')
        const contextMatches =
            shopperContextCountry === shipToCountry.id &&
            shopperContextCurrency === shipToCountry.currency &&
            shopperContextLocale === locale.id

        if (contextMatches) {
            if (countryCodeStillSetting === shipToCountry.id) {
                clearLocalStorageJSONItem('countryCode')
            }
            return
        }

        const persistenceKey = [
            usid,
            site.id,
            locale.id,
            shipToCountry.id,
            shipToCountry.currency
        ].join(':')

        if (lastPersistedContextRef.current === persistenceKey) return
        lastPersistedContextRef.current = persistenceKey

        persistShopperContextCountry(shipToCountry)
            .then(() => {
                if (countryCodeStillSetting === shipToCountry.id) {
                    clearLocalStorageJSONItem('countryCode')
                }
            })
            .catch(() => {
                lastPersistedContextRef.current = null
            })
    }, [
        persistOnShipToCountryChange,
        isShopperContextLoading,
        usid,
        site?.id,
        locale?.id,
        shipToCountry?.id,
        shipToCountry?.currency,
        shopperContext?.geoLocation?.countryCode,
        shopperContext?.geoLocation?.country,
        shopperContext?.customQualifiers?.currency,
        shopperContext?.customQualifiers?.locale
    ])

    // Calculate currentShipToCountry for external use
    const currentShipToCountry = getShipToCountry(
        shipToCountry?.id || previousShipToCountryRef.current?.id,
        isUS
    )
    // Return the function and any data you need to use externally
    return {
        updateShopperContextAfterLogout: (requestedShipToCountry) =>
            updateShopperContextAfterLogout({
                currentShipToCountry: requestedShipToCountry?.id
                    ? requestedShipToCountry
                    : currentShipToCountry,
                locale,
                updateShopperContext,
                createShopperContext,
                getUsidWhenReady,
                site,
                shopperContext
            })
    }
}

export default useUpdateShopperContext
